import React, { useEffect, useRef, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { useAuth, signOut } from '../AuthContext';
import { useJobs, jobIsActive, useLiveConfig } from '../data/firestore';
import { useMyOrgs } from '../data/entities';
import { ThemeToggle } from '../theme';
import ConsoleTools from './ConsoleHeader';
import WorkNav, { FOUNDERS } from './WorkNav';
import { openBugReport, openFeatureRequest } from './BugReportDialog';

// Topbar for the platform app (app.nventr.io). Deliberately NOT the marketing
// navbar: once signed in you are "on platform" — console nav only; the
// marketing pages sit under one Site group that points at nventr.io.
//
// Two modes (decisions ux-single-nav-in-org, signed-in-work-bar):
// - Outside an org (/, /orgs, /jobs, /admin/*): the WorkNav — the same
//   signed-in bar the public host shows (Console · Jobs · Help · Admin ·
//   Site · theme · account), with Site links external.
// - Inside /orgs/:orgId/*: a utility strip. Brand → /orgs, the org switcher,
//   console search (⌘K) + notifications, theme, and a user menu holding
//   Jobs / Fleet / Brain / nventr.io / sign out. The sidebar (OrgShell) is the
//   only thing that moves between modules; this bar never does. Under 700px
//   the strip's burger opens the sidebar as a drawer.

export const toggleSidebar = () => window.dispatchEvent(new CustomEvent('nv:toggle-sidebar'));

export default function PlatformNav() {
  const { user } = useAuth();
  const member = !!user && !user.isAnonymous;
  const jobs = useJobs(100, member);
  const activeJobs = (jobs || []).filter(jobIsActive).length;
  const inOrg = useMatch('/orgs/:orgId/*');
  const orgId = inOrg?.params?.orgId;
  const founder = member && FOUNDERS.includes(user.email);
  const { doc: liveCfg } = useLiveConfig();
  const handbookReader = founder || (member && (liveCfg?.docs?.engineeringReaders || []).includes(user.email));

  if (member && orgId) {
    return (
      <OrgStrip
        orgId={orgId}
        user={user}
        founder={founder}
        handbookReader={handbookReader}
        activeJobs={activeJobs}
      />
    );
  }

  if (member) return <WorkNav external />;

  // Signed out (login, welcome): brand + theme only.
  return (
    <nav className="cs-nav scrolled cs-nav-work">
      <Link to="/" className="cs-brand">
        <img src="/logo.png" alt="nventr" className="cs-brand-logo" />
        <span className="cs-help" style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>
          Platform
        </span>
      </Link>
      <div className="cs-nav-spacer" />
      <div className="cs-nav-user">
        <a href="https://nventr.io" className="cs-nav-contact">nventr.io ↗</a>
        <ThemeToggle />
      </div>
    </nav>
  );
}

// Also used by the public-host Navbar inside /orgs/:orgId/* — both hosts
// serve the console, and the strip is the same on either.
export function OrgStrip({ orgId, user, founder, handbookReader = founder, activeJobs }) {
  const navigate = useNavigate();
  const orgs = useMyOrgs();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [menu]);

  const initial = (user.displayName || user.email || '?').trim().charAt(0).toUpperCase();
  const known = (orgs || []).some((o) => o.id === orgId);

  return (
    <nav className="cs-nav scrolled cs-nav-strip" aria-label="Platform">
      <button type="button" className="cs-nav-burger cs-nav-strip-burger" onClick={toggleSidebar} aria-label="Open modules menu">☰</button>
      <Link to="/orgs" className="cs-brand cs-brand-strip" title="All organizations">
        <img src="/logo.png" alt="nventr" className="cs-brand-logo" />
      </Link>
      <select
        className="cs-input pf-org-switcher"
        value={orgId}
        aria-label="Organization"
        onChange={(e) => {
          if (e.target.value === '__all') navigate('/orgs');
          else navigate(`/orgs/${e.target.value}`);
        }}
      >
        {!known && <option value={orgId}>{orgId}</option>}
        {(orgs || []).map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
        <option value="__all">All organizations…</option>
      </select>
      <ConsoleTools orgId={orgId} />
      <div className="cs-nav-spacer" />
      <div className="cs-nav-user cs-nav-user-strip" ref={menuRef}>
        <ThemeToggle />
        <button
          type="button"
          className={`cs-nav-avatar${menu ? ' is-open' : ''}`}
          onClick={() => setMenu((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={menu}
          aria-label="Account menu"
          title={user.email}
        >
          {initial}
          {activeJobs > 0 && <span className="cs-nav-jobs-badge cs-nav-avatar-badge">{activeJobs}</span>}
        </button>
        {menu && (
          <div className="cs-nav-menu-pop" role="menu">
            <div className="cs-nav-menu-who">{user.email}</div>
            <Link to="/support" role="menuitem" onClick={() => setMenu(false)}>Support</Link>
            <button type="button" role="menuitem" onClick={() => { setMenu(false); openBugReport(); }}>Report a bug</button>
            <Link to="/bugs" role="menuitem" onClick={() => setMenu(false)}>Your bug reports</Link>
            <button type="button" role="menuitem" onClick={() => { setMenu(false); openFeatureRequest(); }}>Request a feature</button>
            <Link to="/features" role="menuitem" onClick={() => setMenu(false)}>Feature board</Link>
            <Link to="/jobs" role="menuitem" onClick={() => setMenu(false)}>
              Jobs {activeJobs > 0 && <span className="cs-nav-jobs-badge">{activeJobs}</span>}
            </Link>
            {founder && (
              <>
                <Link to="/admin/apps" role="menuitem" onClick={() => setMenu(false)}>Fleet</Link>
                <Link to="/admin/support" role="menuitem" onClick={() => setMenu(false)}>Support desk</Link>
                <Link to="/admin/bugs" role="menuitem" onClick={() => setMenu(false)}>Bug reports</Link>
                <Link to="/admin/helpdesk" role="menuitem" onClick={() => setMenu(false)}>Help desk</Link>
                <Link to="/admin/features" role="menuitem" onClick={() => setMenu(false)}>Feature requests</Link>
                <Link to="/admin/brain" role="menuitem" onClick={() => setMenu(false)}>Brain</Link>
                <Link to="/admin/films" role="menuitem" onClick={() => setMenu(false)}>Films</Link>
              </>
            )}
            {handbookReader && <Link to="/admin/engineering" role="menuitem" onClick={() => setMenu(false)}>Handbook</Link>}
            <a href="https://nventr.io" role="menuitem">nventr.io ↗</a>
            <button type="button" role="menuitem" onClick={() => signOut()} data-agent-noclick>Sign out</button>
          </div>
        )}
      </div>
    </nav>
  );
}
